import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsService } from 'angular2-notifications';
import { BehaviorSubject, filter, first, last, Observable, take } from 'rxjs';
import { Constants } from '../_consts/constants';
import { NgxSmartModalComponent, NgxSmartModalService } from 'ngx-smart-modal';
import { ErrorService } from './error.service';
import { ISGroup } from './objects/s-group';
import { HttpMethod, HttpPlus } from './http-plus';
import { HttpClient } from '@angular/common/http';
import { Fce } from './fce';
import { IFrameComponent } from './comps/i-frame/i-frame.component';
import { LogModalComponent } from './modals/log-modal/log-modal.component';
import { YesNoComponent } from './modals/yes-no/yes-no.component';
import { ProgressModalComponent } from './modals/progress-modal/progress-modal.component';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class SService implements OnDestroy {

  protected _isLoading = new BehaviorSubject<boolean>(false);
  protected _login = new BehaviorSubject<any>(null);
  protected _refreshingToken = new BehaviorSubject<boolean>(false);
  protected _rights = new BehaviorSubject<any>(null);
  public _anyStore: any = {}

  public messages = new BehaviorSubject<any>(null);
  public contextMenuOpts = new BehaviorSubject<any>(null);
  public loadedIframe: IFrameComponent
  public transService: TranslateService;
  protected subscribtions = []
  protected uid = 1
  protected _isSubModule: boolean = false
  public deviceType = null
  public isMobileConst: boolean = false

  constructor(public route: ActivatedRoute, public router: Router, protected notifier: NotificationsService, protected modal: NgxSmartModalService,
    protected errorService: ErrorService) {

    this._isSubModule = window.top && (window.top as any).is_top
    this.setAny(Constants.BROADCAST, null)
    this.deviceType = this.getDeviceType()
    this.isMobileConst = this.isMobile()
  }

  reset() {
    this.storageDelete('login');
    this.login = null
    this.rightsAll = null
    const broadcast = this._anyStore[Constants.BROADCAST]
    this._anyStore = {}
    if (broadcast)
      this._anyStore[Constants.BROADCAST] = broadcast
    this.subscribtions?.forEach(s => s.unsubscribe())

  }

  isLoggedIn(andRight = false): boolean {
    return this.login && this.login.id_user && (!andRight || !!this.rightsAll)
  }

  setLoading(isLoading: boolean) {
    if (this.isSubModule) {
      window.top.postMessage({
        type: 'LOADING',
        data: isLoading
      }, '*')
    } else
      requestAnimationFrame(() => {
        this._isLoading.next(isLoading);
      })
  }

  get loading() {
    return this._isLoading.value || this._refreshingToken.value
  }

  t(text) {
    return (text && this.transService) ? this.transService.instant(`${text}`) : null
  }

  set login(login) {
    this._login.next(login)
  }

  get login() {
    return this._login.value
  }

  get loginWatch() {
    return this._login
  }

  set refreshingToken(isRefreshing) {
    this._refreshingToken.next(isRefreshing)
  }

  get refreshingToken() {
    return this._refreshingToken.value
  }

  get refreshingTokenWatch() {
    return this._refreshingToken
  }

  set rightsAll(rights: any) {
    this._rights.next(rights)
  }

  get rightsAll() {
    return this._rights.value
  }

  get rightsAllWatch() {
    return this._rights
  }

  get rightsAllFirstNotNull(): Observable<any> {
    return this.rightsAllWatch.pipe(
      first(rightsAllValue => !!rightsAllValue)
    )
  }

  get rights(): ISGroup {
    if (this.isSubModule)
      return this._login.value || {}
    else
      return (this._rights.value && this._rights.value[Constants.MOD_BASE]) || {}
  }

  get isSubModule() {
    return this._isSubModule
  }

  // ========== RIGHTS HELPERS ==========

  /**
   * Check if user has access to a module (any group assigned)
   */
  canView(module: string): boolean {
    return !!this._rights.value?.[module]
  }

  /**
   * Check if user can create in a module
   */
  canCreate(module: string): boolean {
    return !!this._rights.value?.[module]?.r_create
  }

  /**
   * Check if user can edit an item in a module
   * - Own items (created_by === current user) can always be edited
   * - Other's items require r_edit permission
   */
  canEdit(module: string, createdBy?: number): boolean {
    if (!this._rights.value?.[module]) return false
    // Own items - always editable
    if (createdBy && createdBy === this._login.value?.id_user) return true
    // Others' items - need r_edit
    return !!this._rights.value[module].r_edit
  }

  /**
   * Check if user can delete in a module
   */
  canDelete(module: string): boolean {
    return !!this._rights.value?.[module]?.r_close
  }

  /**
   * Check specific right in a module
   */
  hasRight(module: string, right: 'r_create' | 'r_edit' | 'r_close' | 'r_extra_1' | 'r_extra_2' | 'r_extra_3' | 'r_extra_4' | 'r_extra_5' | 'r_extra_6'): boolean {
    return !!this._rights.value?.[module]?.[right]
  }

  /**
   * Get module rights object
   */
  getModuleRights(module: string): ISGroup | null {
    return this._rights.value?.[module] || null
  }

  getUid() {
    return ++this.uid
  }

  async getCached(http: HttpPlus, url: string, httpType: HttpMethod, params: any = null, hideLoader: boolean = true, force: boolean = false) {
    if (!force && this.getAny(`cached_${url}`))
      return Fce.copy(this.getAny(`cached_${url}`))

    let response: any
    switch (httpType) {
      case HttpMethod.POST:
        if (hideLoader)
          params = { ...params, hideLoader }
        response = await http.post(url, params)
        break;
      case HttpMethod.GET:
        response = await http.get(url, params, hideLoader, {})
        break;
    }
    this.setAny(`cached_${url}`, Fce.copy(response))
    return response
  }

  resetCached(url: string, isPrefix: boolean = false) {
    if (isPrefix) {
      const keys = Object.keys(this._anyStore)
      keys.forEach(k => {
        if (`${k}`.startsWith(`cached_${url}`))
          this.setAny(k, null)
      })
    } else {
      this.setAny(`cached_${url}`, null)
    }
  }

  setAny(key, value) {
    if (this._anyStore[key])
      this._anyStore[key].next(value)
    else
      this._anyStore[key] = new BehaviorSubject<any>(value)
  }

  getAny(key, defVal = null) {
    return (this._anyStore[key] && this._anyStore[key].value) || defVal
  }

  getAnyWatch(key): BehaviorSubject<any> {
    if (!this._anyStore[key])
      this.setAny(key, null);
    return this._anyStore[key]
  }

  broadcast(type, callback) {
    this.setAny(Constants.BROADCAST, null)
    this.setAny(Constants.BROADCAST, { type, callback })
  }

  setTranslate(translate) {
    this.transService = translate;
    this.transService.setDefaultLang(Constants.DEF_LANGUAGE);

    const lang = this.storageGet('lang');
    if (lang && Constants.LANGUAGES.includes(lang))
      this.transService.use(lang);
    else
      this.transService.use(Constants.DEF_LANGUAGE);
    moment.locale(lang);
  }

  addSubstribtion(s) {
    this.subscribtions.push(s)
  }

  success(head: string = "saved", text: string = null, timeOut = 2000) {
    if (this.isSubModule) {
      window.top.postMessage({
        type: 'NOTIFICATION',
        data: { type: "SUCCESS", head: this.t(head), text: this.t(text) }
      }, '*')
    } else
      this.notifier.success(this.t(head), this.t(text), { timeOut });
  }

  info(head: string, text: string = null, timeOut = 4000) {
    if (this.isSubModule) {
      window.top.postMessage({
        type: 'NOTIFICATION',
        data: { type: "INFO", head: this.t(head), text: this.t(text), timeOut }
      }, '*')
    } else
      this.notifier.info(this.t(head), this.t(text), { timeOut });
  }

  warning(head: string, text: string = null, timeOut = 4000) {
    if (this.isSubModule) {
      window.top.postMessage({
        type: 'NOTIFICATION',
        data: { type: "WARNING", head: this.t(head), text: this.t(text), timeOut }
      }, '*')
    } else
      this.notifier.warn(this.t(head), this.t(text), { timeOut });
  }

  error(head: string, text: string = null) {
    if (this.isSubModule) {
      window.top.postMessage({
        type: 'NOTIFICATION',
        data: { type: "ERROR", head: this.t(head), text: text }
      }, '*')
    } else
      this.notifier.error(this.t(head), text, { timeOut: 6000 });
  }

  showLog(data: any) {
    const modal = this.modal.create('logModal', LogModalComponent, {
      customClass: "modal-dialog modal-fullscreen",
      dismissable: true
    })
    modal.setData(data, true)
    modal.open()
  }


  logError(e) {
    console.error(e)
    const head = e.status === 0 ? 'Cannot connect to server' : (`Server error: ${e.status || ''}`)
    let text = "";
    if (e.statusText && e.statusText != "OK") {
      if (e.status === 0 && e.statusText === "Unknown Error")
        text += `Cannot reach the server.<br>Verify your connection to network.<br>`
      else
        text += `${e.statusText}<br>`
    }
    if (e.error && typeof e.error == 'object' && e.status > 200 && e.status < 500 && !(e.error instanceof Blob)) {
      for (const key in e.error) {
        if (['trace'].includes(key))
          continue;
        let tmp = ""
        if (isNaN(parseInt(key)))
          tmp += `${key}: `
        if (Array.isArray(e.error[key])) {
          tmp += e.error[key].join("<br>");
        } else
          tmp += e.error[key]
        if (tmp.length < 512)
          text += `<br>${tmp.replace(/validation./g, "")}`
      }
    } else if (e.error && typeof e.error == 'string' && e.error != e.message)
      text += `<br>${e.error}`
    else if (e.message)
      text += `<br>${e.message}`

    if ([406, 409, 422].includes(e.status))
      this.warning('warning.warning', text)
    else if (e.status == 401) {
      this.login = null
      if (this.router.url.includes("login")) {
        this.warning("warning.credentials");
      } else {
        this.router.navigate(["/login"]);
        this.error("You are not logged in");
      }
    }
    else if (e.status == 200) {
      //Weird JSON parser error
      this.error(head, text);
      this.errorService.handleError(e)
    } else
      this.error(head, text);
  }


  ngOnDestroy(): void {
    this.subscribtions.forEach(s => s.unsubscribe())
  }


  modalYesNo(head, text = null, onYes, onNo = null, size = "md", closeEqNo: boolean = false) {
    const modal = this.modal.create('YesNo' + this.getUid(), YesNoComponent, {
      customClass: "modal-dialog modal-" + size,
      dismissable: false
    })
    modal.setData({ head, text }, true)
    modal.open()
    modal.onAnyCloseEventFinished.pipe(take(1)).subscribe((modal: NgxSmartModalComponent) => {
      const data = modal.getData()
      console.log("MODAL CLOSED", modal, data)
      switch (data.result) {
        case 'YES':
          if (onYes)
            onYes()
          break;
        case 'NO':
          if (onNo)
            onNo()
          break;
        default:
          if (closeEqNo && onNo)
            onNo()
          break;
      }

    })
  }

  confirm(head, text = null, size = "md"): Promise<boolean> {
    return new Promise((resolve) => {
      const modal = this.modal.create('YesNo' + this.getUid(), YesNoComponent, {
        customClass: "modal-dialog modal-" + size,
        dismissable: false
      })
      modal.setData({ head, text }, true)
      modal.open()
      modal.onAnyCloseEventFinished.pipe(take(1)).subscribe((modal: NgxSmartModalComponent) => {
        const data = modal.getData()
        resolve(data.result === 'YES')
      })
    })
  }

  modalInfo(head, text = null, onOk = null, size = "md") {
    const modal = this.modal.create('YesNo' + this.getUid(), YesNoComponent, {
      customClass: "modal-" + size,
      dismissable: false
    })
    modal.setData({ head, text, typeInfo: true }, true)
    modal.open()
    modal.onAnyCloseEventFinished.pipe(take(1)).subscribe(() => {
      if (onOk)
        onOk()
    })
  }

  modalProgress(type: HttpMethod, url: string, params: any, onFinish: Function) {
    const modal = this.modal.create('ProgressModal' + this.getUid(), ProgressModalComponent, {
      customClass: "modal-dialog modal-md",
      dismissable: false
    })
    modal.setData({ type, url, params }, true)
    modal.open()
    modal.onAnyCloseEventFinished.pipe(take(1)).subscribe((modal: NgxSmartModalComponent) => {
      const data = modal.getData()
      if (data.result) {
        onFinish(data.result)
      }
    })
  }


  contextMenu(options, event) {
    this.contextMenuOpts.next({ options, event })
  }

  async writeClipboard(text: string, showInfo: boolean = true) {
    if (Fce.empty(text))
      return
    const clipboard = this.isSubModule ? window.top.navigator.clipboard : window.navigator.clipboard
    if (!clipboard)
      this.warning("Cannot access clipboard. Please enable clipboard access in your browser settings")

    let saved = false
    try {
      await clipboard.writeText(text)
      const read = await clipboard.readText();
      if (!Fce.empty(read))
        saved = true
    } catch (error) {
      console.error(error)
    }
    if (saved) {
      if (showInfo)
        this.info("copied_to_clipboard", null, 500)
    } else
      this.warning("Cannot access clipboard. Please enable clipboard access in your browser settings")
  }

  getClipboard() {
    try {
      if (this.isSubModule) {
        return window.top.navigator.clipboard.readText();
      } else {
        return navigator.clipboard.readText();
      }
    } catch (error) {
      console.error(error)
      return null
    }
  }

  storageGet(key: string, defVal = null) {
    try {
      return localStorage.getItem(key) ?? defVal
    } catch (error) {
      this.warning("Browser storage is not available", null, 1000)
      return defVal ?? null
    }
  }

  storageSet(key: string, value: any) {
    try {
      localStorage.setItem(key, value)
    } catch (error) {
      this.warning("Browser storage is not available", null, 1000)
      return null
    }
  }

  storageDelete(key: string) {
    try {
      return localStorage.removeItem(key)
    } catch (error) {
      this.warning("Browser storage is not available", null, 1000)
      return null
    }
  }

  loadActiveTab(tabName, withModule: boolean, defVal: number = 0) {
    if (withModule) {
      if (!this.login?.module)
        return defVal
      tabName = `${this.login.module}_${tabName}`
    }
    return parseInt(this.storageGet(tabName) || `${defVal}`)
  }

  saveActiveTab(tabName, withModule: boolean, value: number = 0) {
    if (withModule) {
      if (!this.login?.module)
        return
      tabName = `${this.login.module}_${tabName}`
    }
    this.storageSet(tabName, value)
  }

  navigateForceRefresh(...paths) {
    this.router.navigateByUrl('/reload', { skipLocationChange: true }).then(() => {
      this.router.navigate([...paths]);
    });
  }

  navigateForceRefreshWait(...paths) {
    this.router.navigateByUrl('/reload', { skipLocationChange: true }).then(() => {
      requestAnimationFrame(() => {
        this.router.navigate([...paths]);
      });
    });
  }

  async checkForUpdates(httpClient: HttpClient) {
    const getVersion = (doc) => {
      const scripts = doc.querySelectorAll('script')
      for (let i = 0; i < scripts.length; i++) {
        const tmp = scripts[i].src?.split("/").pop()
        if (tmp?.includes('main.')) {
          return tmp.split('.')[1]
        }
      }
      return 0
    }

    const loadedVersion = getVersion(document)
    let serverVersion: any = 0

    const salt = (new Date()).getTime();
    let serverHtml: any = await httpClient.get(`index.html?${salt}`, { responseType: 'text' }).toPromise().catch((e) => this.logError(e));
    if (serverHtml) {
      const iStart = serverHtml.indexOf("<body>")
      const iEnd = serverHtml.lastIndexOf("</body>")
      if (iStart > -1 && iEnd > -1) {
        serverHtml = serverHtml.substring(iStart + 6, iEnd);
        const doc = document.createElement("body")
        doc.innerHTML = serverHtml
        serverVersion = getVersion(doc)
      }
      if (loadedVersion != serverVersion) {
        console.log("DIFF VERSION")
        console.log("VERSION CONTROL", loadedVersion, serverVersion)
        this.checkForUpdatesDialog()
      }
    } else {
      this.warning("Cannot connect to server!");
    }
  }

  checkForUpdatesDialog(doReload: boolean = false) {
    if (doReload) {
      this.storageSet('lastUpdate', moment().format())
      if (this.isSubModule) {
        if (this.loadedIframe)
          this.loadedIframe.reloadIframe()
        else
          (window.top as any).location.reload()
      } else
        window.location.reload()
    } else {
      this.storageSet('updating', true)
      const test = this.storageGet('updating')
      if (Fce.parseBool(test)) {
        //One time per day do it automatically
        const lastUpdate = this.storageGet('lastUpdate')
        if (!lastUpdate || !moment(lastUpdate).isSame(moment(), 'day')) {
          this.checkForUpdatesDialog(true)
          return
        }
      }
      this.modalYesNo("New version is available", `Do you want to refresh the ${this.isSubModule ? 'module' : 'application'}?`, () => {
        this.checkForUpdatesDialog(true)
      })
    }
  }

  getDeviceType(): 'web' | 'android' | 'ios' {
    const userAgent = navigator.userAgent;

    if (/android/i.test(userAgent)) {
      return 'android';
    } else if (/iPad|iPhone|iPod/.test(userAgent) && /webkit/i.test(userAgent) && /Mobile/i.test(userAgent)) {
      return 'ios';
    } else {
      return 'web';
    }
  }

  isMobile(): boolean {
    return (window.innerWidth < Constants.MOBILE_FROM_W)
  }
}


